// Sugalarm — native auth bridge (cycle 41).
//
// The iOS Siri App Intents (`LogInsulinIntent` / `CurrentGlucoseIntent`)
// run *outside* the Capacitor webview, so they cannot read the JWT out
// of `localStorage`. The standing pattern that works across the
// process boundary on iOS: the webview writes the bearer token into a
// shared App Group container (`group.dev.sugalarm.app.shared`), and the
// intent process reads it back. The native writer-side seam is the
// `AuthBridge` Capacitor plugin (`mobile/ios/App/App/AuthBridge.swift`);
// this module is the TS caller.
//
// Same posture cycle 28 used for `@capacitor/push-notifications`:
// `web/` keeps **zero** compile-time dependency on `@capacitor/*`. The
// `@capacitor/core` import is dynamic + `/* @vite-ignore */` so Rollup
// never tries to resolve it at build time; it resolves at runtime
// against the Capacitor-injected runtime on a native device. On every
// non-Capacitor surface (kiosk, browser, PWA) `isCapacitorNative()` is
// false and every entry point here no-ops.
//
// Both exported calls are fire-and-forget. A rejected Promise must
// never block the auth flow — callers in `lib/auth.ts` do not await.

import { capacitorPlatform, isCapacitorNative } from "./capacitor";
import { apiUrl } from "../env";

// ── Cycle 268: Android `standard` home-screen widget auth mirror ─────────
//
// The Android widget's background WorkManager worker (cycle 268) cannot
// read the JWT out of the Capacitor WebView storage, so it can't refresh
// the widget on a closed app. We mirror the access+refresh pair (plus the
// API base) into an app-private store the worker CAN read, via new
// `writeAuth`/`clearAuth` methods on the cycle-212 `WidgetBridge` plugin.
// This rides the SAME token-push call sites as the iOS App-Group write
// (`pushTokensToNative` on sign-in/refresh/resume, `clearTokensInNative`
// on sign-out), so the worker's credential stays as fresh as the app's.
// Reached via `window.Capacitor.Plugins` (cycle-138) — null off the
// Android `standard` shell, so iOS / web / Pi / the `kiosk` flavor no-op.

interface WidgetAuthBridge {
  writeAuth: (payload: {
    accessToken: string;
    refreshToken: string;
    apiBase: string;
  }) => Promise<void>;
  clearAuth: () => Promise<void>;
}

/** The Android `standard` WidgetBridge plugin, only when it exposes the
 *  cycle-268 auth methods. Null on iOS, web, Pi, and the kiosk flavor. */
function widgetAuthBridge(): WidgetAuthBridge | null {
  if (!isCapacitorNative() || capacitorPlatform() !== "android") return null;
  const p = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor?.Plugins?.["WidgetBridge"] as Partial<WidgetAuthBridge> | undefined;
  // Older `standard` APKs lack writeAuth/clearAuth — null-check so a
  // pre-268 build degrades to the foreground-only widget.
  if (p && typeof p.writeAuth === "function" && typeof p.clearAuth === "function") {
    return p as WidgetAuthBridge;
  }
  return null;
}

/** The HTTPS API base the widget worker fetches against — the SAME Fly
 *  origin the SPA uses (env.ts), never a hardcoded literal here. */
function widgetApiBase(): string {
  // apiUrl("/") → "https://sugalarm.fly.dev/" on native; trim the slash.
  return apiUrl("/").replace(/\/+$/, "");
}

/** Shape the native `AuthBridge.setTokens` plugin method expects.
 *  `expiresAt` is the JWT `exp` claim — Unix epoch **seconds** — so
 *  the Swift side can `Date(timeIntervalSince1970:)` it directly. */
interface NativeTokenPayload {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  userId: string;
}

interface AuthBridgePlugin {
  setTokens: (payload: NativeTokenPayload) => Promise<void>;
  clearTokens: () => Promise<void>;
  /** Cycle 144 — present only on mobile shell ≥ 0.115.5; older builds'
   *  bridge proxies simply lack the method (callers must null-check). */
  getTokens?: () => Promise<{ accessToken?: string; refreshToken?: string }>;
}

/** Decode a JWT payload without verifying the signature (the server is
 *  authoritative — this is only used to surface `exp` / `sub` to the
 *  native side). Returns `{}` on any malformed input rather than
 *  throwing, so a decode hiccup degrades to "always refresh". */
function decodeJwtPayload(token: string): { exp?: number; sub?: string } {
  try {
    const part = token.split(".")[1];
    if (!part) return {};
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const parsed = JSON.parse(json) as { exp?: unknown; sub?: unknown };
    return {
      exp: typeof parsed.exp === "number" ? parsed.exp : undefined,
      sub: typeof parsed.sub === "string" ? parsed.sub : undefined,
    };
  } catch {
    return {};
  }
}

async function getAuthBridge(): Promise<AuthBridgePlugin | null> {
  if (!isCapacitorNative()) return null;
  try {
    // The pure-remote webview can't resolve a bare `import("@capacitor/core")`
    // at runtime (no bundler) — the bridge-injected `window.Capacitor.Plugins`
    // global is the reliable path to natively-registered plugins.
    const plugin = (
      window as {
        Capacitor?: { Plugins?: { AuthBridge?: AuthBridgePlugin } };
      }
    ).Capacitor?.Plugins?.AuthBridge;
    return plugin ?? null;
  } catch (err) {
    console.warn("[authBridge] AuthBridge plugin lookup failed", err);
    return null;
  }
}

/** Mirror the freshly-stored JWT pair into the iOS App Group container
 *  so the Siri App Intents can authenticate. No-ops off-native and on
 *  any failure. Called from `lib/auth.ts`'s `setJwt` after every
 *  successful login / register / refresh. */
export function pushTokensToNative(tokens: {
  access_token: string;
  refresh_token: string;
}): void {
  void (async () => {
    const bridge = await getAuthBridge();
    if (bridge) {
      const { exp, sub } = decodeJwtPayload(tokens.access_token);
      try {
        await bridge.setTokens({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          // Fall back to 0 (treated as "already expired" → the intent
          // refreshes before its first call) if the claim is missing.
          expiresAt: exp ?? 0,
          userId: sub ?? "",
        });
      } catch (err) {
        console.warn("[authBridge] setTokens failed", err);
      }
    }
    // Cycle 268: mirror the pair to the Android `standard` widget worker.
    const widget = widgetAuthBridge();
    if (widget) {
      try {
        await widget.writeAuth({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          apiBase: widgetApiBase(),
        });
      } catch (err) {
        console.warn("[authBridge] widget writeAuth failed", err);
      }
    }
  })();
}

/** Cycle 144 — session restore. When the webview's localStorage was
 *  evicted (iOS storage pressure / container migration) the App Group
 *  still holds the JWT pair (it's written on every login/refresh for
 *  the Siri intents). Returns the pair when both halves are present,
 *  else null. No-ops to null off-native, on pre-0.115.5 shells (no
 *  `getTokens` on the bridge proxy), and after sign-out (`clearTokens`
 *  wiped the container — a signed-out session can't be resurrected).
 *  The restored access token may be expired; the apiClient's 401
 *  refresh dance handles that transparently. */
export async function restoreTokensFromNative(): Promise<{
  access_token: string;
  refresh_token: string;
} | null> {
  const bridge = await getAuthBridge();
  if (!bridge || typeof bridge.getTokens !== "function") return null;
  try {
    const t = await bridge.getTokens();
    if (t?.accessToken && t?.refreshToken) {
      return { access_token: t.accessToken, refresh_token: t.refreshToken };
    }
  } catch (err) {
    console.warn("[authBridge] getTokens failed", err);
  }
  return null;
}

/** Wipe the App Group token container. Called from `lib/auth.ts`'s
 *  `clearAll` (sign-out and auth-teardown on a dead refresh token) so a
 *  signed-out phone can never log a dose via Siri. No-ops off-native. */
export function clearTokensInNative(): void {
  void (async () => {
    const bridge = await getAuthBridge();
    if (bridge) {
      try {
        await bridge.clearTokens();
      } catch (err) {
        console.warn("[authBridge] clearTokens failed", err);
      }
    }
    // Cycle 268: wipe the Android widget worker's mirrored credential so a
    // signed-out phone's worker has nothing to fetch with.
    const widget = widgetAuthBridge();
    if (widget) {
      try {
        await widget.clearAuth();
      } catch (err) {
        console.warn("[authBridge] widget clearAuth failed", err);
      }
    }
  })();
}
